// HR › Payroll review: the holiday decisions of one employee, and the dialog in which a payroll manager enters the
// amount for a day the system cannot decide (Holiday Calendar Phase C, report 39 §N). The server owns every amount,
// every "needs review" and whether a reference is a government minimum; this only shows them and sends the review.
import { useId, useState } from "react";
import { Link, useInRouterContext } from "react-router-dom";

import { Button, Dialog, Field, Input, Money, Notice, Status, Textarea, cn, focusRing, formatMoney, plainError } from "../../../../components/ui";
import { businessApi } from "../../../../services/api";
import {
  HOLIDAY_AUTO_NOTE, HOLIDAY_REVIEW_LIMITS, HOLIDAY_STALE_MESSAGE, holidayAlreadyPaidText, holidayLineView, holidayReviewErrors,
} from "../holidayPayView";

const link = cn("rounded-control font-medium text-accent hover:underline", focusRing);

/** A router link where there is a router (the app), a plain link otherwise (isolated component tests). */
function AppLink({ to, children }) {
  const inRouter = useInRouterContext();
  return inRouter ? <Link to={to} className={link}>{children}</Link> : <a href={to} className={link}>{children}</a>;
}

const reviewedDay = (value) => (value
  ? new Date(value).toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })
  : "");

/** "Government minimum: ₱802.50" or "For reference: ₱600.00" — the two are never worded alike. */
export function ReferenceLine({ reference, className }) {
  if (!reference) return null;
  return (
    <p className={cn("text-meta text-secondary", className)}>
      {reference.label}: <span className="tabular-nums text-foreground">{formatMoney(reference.cents)}</span>
      {reference.isMinimum ? ". A lower amount is not accepted." : ". What the standard rules would give; you decide the amount."}
    </p>
  );
}

/**
 * The amount and reason for one holiday day. A refusal stays in the dialog in the server's words. When the payroll
 * was recalculated or changed in the meantime, the dialog says so and offers a refresh; it never resends by itself.
 */
export function HolidayReviewDialog({ periodId, memberName, line, onSaved, onClose, onStale }) {
  const view = holidayLineView(line);
  const formId = useId();
  const [form, setForm] = useState({ amount: view.state === "reviewed" && view.amountCents != null ? (view.amountCents / 100).toFixed(2) : "", reason: "" });
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState("");
  const [stale, setStale] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    setFailure("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving || stale) return;
    const checked = holidayReviewErrors(form);
    setErrors(checked.errors);
    setFailure("");
    const first = ["amount", "reason"].find((key) => checked.errors[key]);
    if (first) {
      document.getElementById(`${formId}-${first}`)?.focus();
      return;
    }
    setSaving(true);
    try {
      await businessApi.recordPayrollHolidayReview(periodId, { lineId: view.id, amountCents: checked.cents, reason: checked.reason });
      // The review closes this dialog and reloads; nothing more is set here.
      onSaved(view);
      return;
    } catch (err) {
      const code = err?.serverCode;
      if (err?.status === 404 || code === "HOLIDAY_REVIEW_STALE" || code === "HOLIDAY_REVIEW_NOT_OPEN" || code === "HOLIDAY_REVIEW_NOT_NEEDED") {
        // The line this screen holds no longer exists or no longer needs this review. Nothing is resent.
        setStale(true);
      } else if (code === "HOLIDAY_BELOW_MINIMUM") {
        setErrors({ amount: plainError(err, "This amount is below the government minimum for this day.") });
        document.getElementById(`${formId}-amount`)?.focus();
      } else {
        setFailure(err?.status === 403
          ? "You can't enter a reviewed amount. Ask someone who manages payroll."
          : plainError(err, "The reviewed amount was not saved. Try again."));
      }
    }
    setSaving(false);
  };

  const facts = [
    ["Employee", memberName],
    ["Day", `${view.title} · ${view.kind}`],
    ["Date", view.dateText],
    ["Attendance", view.facts],
  ];

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open && !saving) (stale ? onStale : onClose)(); }}
      title={`Review holiday pay for ${memberName}`}
      description={`${view.title}, ${view.dateText}`}
      footer={stale ? (
        <Button variant="primary" onClick={onStale}>Refresh</Button>
      ) : (
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" loading={saving}>Save reviewed amount</Button>
        </>
      )}
    >
      <form id={formId} onSubmit={submit} noValidate className="space-y-4">
        <dl className="divide-y divide-subtle border-y border-subtle text-body-sm">
          {facts.map(([label, value]) => (
            <div key={label} className="flex flex-col gap-0.5 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
              <dt className="shrink-0 text-secondary">{label}</dt>
              <dd className="min-w-0 break-words text-foreground sm:text-right">{value}</dd>
            </div>
          ))}
        </dl>
        {view.reason ? (
          <div className="text-body-sm">
            <p className="text-label text-foreground">Why a person decides this day</p>
            <p className="mt-0.5 text-secondary">{view.reason}</p>
          </div>
        ) : null}
        {stale ? (
          <Notice tone="warning" title="Out of date" role="alert">{HOLIDAY_STALE_MESSAGE}</Notice>
        ) : (
          <>
            {view.reference ? (
              <div className="flex flex-col gap-2 rounded-control bg-surface-sunken px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                <ReferenceLine reference={view.reference} className="text-body-sm" />
                <Button size="sm" disabled={saving} className="shrink-0"
                  onClick={() => { setForm((current) => ({ ...current, amount: (view.reference.cents / 100).toFixed(2) })); setErrors((current) => ({ ...current, amount: undefined })); }}>
                  Use this amount
                </Button>
              </div>
            ) : (
              <p className="text-body-sm text-secondary">Apparify has no reference amount for this day. {holidayAlreadyPaidText(line)}</p>
            )}
            <Field label="Holiday pay for this day (₱)" required id={`${formId}-amount`} error={errors.amount}
              hint={holidayAlreadyPaidText(line)}>
              <Input inputMode="decimal" autoComplete="off" maxLength={14} placeholder="0.00" value={form.amount} onChange={set("amount")} disabled={saving} className="tabular-nums" />
            </Field>
            <Field label="Reason" required id={`${formId}-reason`} error={errors.reason} hint="How you arrived at this amount. Finance sees this.">
              <Textarea rows={3} maxLength={HOLIDAY_REVIEW_LIMITS.reasonMax} value={form.reason} onChange={set("reason")} disabled={saving} />
            </Field>
            {failure ? <Notice tone="danger" title="Not saved" role="alert">{failure}</Notice> : null}
          </>
        )}
      </form>
    </Dialog>
  );
}

/**
 * One employee's holiday days in this payroll. `lines` are stored decisions when the payroll is calculated (they can
 * be reviewed while HR holds it) or the decisions worked out from current facts before that.
 */
export default function PayrollHolidayLines({ member, lines, status, canManage, onReview }) {
  if (!lines?.length) return null;
  const canReview = canManage && status === "computed";
  return (
    <section>
      <h4 className="mb-1 text-label text-foreground">Holidays</h4>
      <ul aria-label="Holidays in this period" className="divide-y divide-subtle border-y border-subtle text-body-sm">
        {lines.map((line) => {
          const view = holidayLineView(line);
          return (
            <li key={view.date} id={`holiday-${member.memberId}-${view.date}`} className="scroll-mt-24 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-foreground">
                    <span className="font-medium">{view.title}</span>
                    <span className="text-secondary"> · {view.kind}</span>
                  </p>
                  <p className="text-meta text-secondary">{view.dateText} · {view.facts}</p>
                </div>
                <div className="shrink-0 whitespace-nowrap text-right tabular-nums text-foreground">
                  {view.state === "review"
                    ? <Status tone="warning" icon className="font-medium">Needs review</Status>
                    : view.amountCents > 0 ? <>+<Money value={view.amountCents} /></> : <span className="text-secondary">No extra</span>}
                </div>
              </div>
              {view.reason ? <p className="mt-1 max-w-[75ch] text-secondary">{view.reason}</p> : null}
              {view.statutory ? <p className="mt-1 max-w-[75ch] text-meta text-secondary">{view.statutory}</p> : null}
              {view.state === "auto" ? null : <ReferenceLine reference={view.reference} className="mt-1" />}
              {view.state === "reviewed" && view.review ? (
                <p className="mt-1 text-secondary">
                  Entered{view.review.reviewedByName ? ` by ${view.review.reviewedByName}` : " by HR"}{view.review.reviewedAt ? ` on ${reviewedDay(view.review.reviewedAt)}` : ""}: {view.review.reason}
                </p>
              ) : null}
              {view.state !== "auto" ? (
                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                  {canReview && view.id ? (
                    <Button size="sm" variant={view.state === "review" ? "primary" : "secondary"} onClick={() => onReview(line)}>
                      {view.state === "review" ? "Review this day" : "Change the reviewed amount"}
                    </Button>
                  ) : view.state === "review" && canManage && status === "collecting_inputs" ? (
                    <span className="text-secondary">Calculate the payroll first; the amount can then be entered here.</span>
                  ) : null}
                  {view.handlingNotSet && canManage ? (
                    <AppLink to={`/business/hr/employees/${member.memberId}/pay`}>Set holiday pay handling</AppLink>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className="mt-1.5 text-meta text-tertiary">
        {HOLIDAY_AUTO_NOTE} Days marked for review are decided by a person.{" "}
        <AppLink to="/business/administration/calendar">Open the calendar</AppLink>
      </p>
    </section>
  );
}
